/**
 * documentShare slice: 문서 공유 상태 관리
 * wehago-conference의 redux/modules/documentShare 구조 기반.
 *
 * resources: 문서 페이지별 이미지 URL 배열 (원본에서 WeDrive API 변환 결과물)
 * selectResource: 현재 선택된 페이지 인덱스
 * objectData: 페이지별 드로잉 데이터 (발표자가 그린 주석)
 * canvasType: 'document' | 'sketch' | null (원본 구조 유지)
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { PageObjectData } from '@/types/store';

// re-export for backwards compatibility
export type { PageObjectData } from '@/types/store';

export interface DocumentShareState {
  isOpen: boolean;         // 문서공유 뷰어 표시 여부
  resources: string[];     // 페이지별 이미지 URL 배열
  fileName: string;        // 공유 파일명
  selectResource: number;  // 현재 페이지 인덱스 (0-based)
  ownerId: string | null;  // Jitsi user ID of document owner
  isOwner: boolean;        // 내가 오너인지 여부
  viewerSidebarOpen: boolean; // 썸네일 사이드바 열림 여부
  canvasType: 'document' | 'sketch' | null; // 현재 캔버스 타입

  // 페이지별 드로잉 데이터 (발표자 주석)
  objectData: PageObjectData[];

  // 비발표자가 수신한 현재 페이지의 드로잉 데이터
  receivedDrawingData: object | null;

  // 도중 참여 플래그
  isModerate: boolean;
}

const initialState: DocumentShareState = {
  isOpen: false,
  resources: [],
  fileName: '',
  selectResource: 0,
  ownerId: null,
  isOwner: false,
  viewerSidebarOpen: false,
  canvasType: null,
  objectData: [],
  receivedDrawingData: null,
  isModerate: false,
};

const documentShareSlice = createSlice({
  name: 'documentShare',
  initialState,
  reducers: {
    setOpen(
      state,
      action: PayloadAction<{
        isOpen: boolean;
        resources?: string[];
        fileName?: string;
        ownerId?: string;
        isOwner?: boolean;
      }>
    ) {
      if (!action.payload.isOpen) {
        // 전체 초기화 — draft 수정 전에 return해야 Immer 에러 방지
        return initialState;
      }
      state.isOpen = true;
      state.resources = action.payload.resources ?? [];
      state.fileName = action.payload.fileName ?? '';
      state.ownerId = action.payload.ownerId ?? null;
      state.isOwner = action.payload.isOwner ?? false;
      state.selectResource = 0;
      state.viewerSidebarOpen = false;
      state.canvasType = 'document';
      // 페이지 수만큼 빈 objectData 초기화
      state.objectData = (action.payload.resources ?? []).map(() => ({
        object: null,
        dataURL: null,
      }));
      state.receivedDrawingData = null;
      state.isModerate = false;
    },

    setSelectResource(state, action: PayloadAction<number>) {
      state.selectResource = action.payload;
    },

    setViewerSidebarOpen(state, action: PayloadAction<boolean>) {
      state.viewerSidebarOpen = action.payload;
    },

    /** 발표자가 특정 페이지에 그린 드로잉 데이터 저장 */
    setPageObjectData(
      state,
      action: PayloadAction<{ pageIndex: number; object: object | null; dataURL: string | null }>
    ) {
      const { pageIndex, object, dataURL } = action.payload;
      if (pageIndex >= 0 && pageIndex < state.objectData.length) {
        state.objectData[pageIndex] = { object, dataURL };
      }
    },

    /** 비발표자가 수신한 드로잉 데이터 설정 */
    setReceivedDrawingData(state, action: PayloadAction<object | null>) {
      state.receivedDrawingData = action.payload;
    },

    /** 캔버스 타입 설정 */
    setCanvasType(state, action: PayloadAction<'document' | 'sketch' | null>) {
      state.canvasType = action.payload;
    },

    /** 도중 참여 플래그 */
    setIsModerate(state, action: PayloadAction<boolean>) {
      state.isModerate = action.payload;
    },

    reset() {
      return initialState;
    },
  },
});

export const documentShareActions = documentShareSlice.actions;
export default documentShareSlice.reducer;
